import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import axios from 'axios'
import type { IFullUserInfo } from '@/models/interfaces'
import { isSelectedClientUserId } from '@/types/observer.types'

export type SessionStatus =
  'idle' | 'active' | 'suspended' | 'revoked' | 'expired' | 'ended' | 'error'

const IDENTITY_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL
const PROFESSIONAL_PORTAL_CLIENT_ID =
  import.meta.env.VITE_APP_IDENTITY_CLIENT_ID || 'tp-professional-portal'

/**
 * Decode JWT payload without verification (we trust the issuer; validation
 * happens server-side on every API call).
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1]
    if (!base64) return null
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

function getTokenExpiry(token: string): number {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return 0
  return payload.exp * 1000
}

function getClaimString(
  payload: Record<string, unknown> | null,
  ...keys: string[]
): string {
  if (!payload) return ''
  for (const key of keys) {
    const val = payload[key]
    if (typeof val === 'string' && val.length > 0) return val
  }
  return ''
}

export const useProfessionalSessionStore = defineStore(
  'professionalSession',
  () => {
    // State
    const professionalToken = ref<string | null>(null)
    const refreshTokenValue = ref<string | null>(null)
    const professionalUserId = ref<string | null>(null)
    const professionalAccountId = ref<string | null>(null)
    const professionalName = ref<string>('')
    const professionalEmail = ref<string>('')
    const professionalRole = ref<string>('')
    const observedParentUserId = ref<string | null>(null)
    const observedParentName = ref<string>('')
    const observedParentEmail = ref<string>('')
    const observedCoparentName = ref<string>('')
    // Full client info projection from GetClientInfo (identity, timezone,
    // date format). Not persisted - refetched once per page load.
    const clientInfo = ref<Partial<IFullUserInfo> | null>(null)
    const sessionStatus = ref<SessionStatus>('idle')
    const tokenExpiresAt = ref<number>(0)
    let refreshTokenRequest: Promise<boolean> | null = null
    let clientInfoRequest: Promise<void> | null = null
    let clientInfoRequestFor: string | null = null
    // Imported dynamically (and cached) to avoid a static circular dependency:
    // observerApiService -> professionalClient -> this store
    let observerApiServiceModule: Promise<
      typeof import('@/httpClient/observerApiService')
    > | null = null

    function loadObserverApiService() {
      observerApiServiceModule ??= import('@/httpClient/observerApiService')
      return observerApiServiceModule
    }

    // Computed
    /**
     * The selectedClientUserId used in all read-only API calls.
     * This is the parent user ID the professional is observing.
     */
    const selectedClientUserId = computed(() => {
      const value = observedParentUserId.value
      return value && isSelectedClientUserId(value) ? value : null
    })
    const isProfessionalSession = computed(
      () =>
        professionalToken.value !== null &&
        sessionStatus.value === 'active' &&
        selectedClientUserId.value !== null
    )

    // Actions

    function clearObservedClientState() {
      observedParentUserId.value = null
      observedParentName.value = ''
      observedParentEmail.value = ''
      observedCoparentName.value = ''
      clientInfo.value = null
      clientInfoRequest = null
      clientInfoRequestFor = null
    }

    /**
     * Initialize session from portal handoff parameters.
     *
     * The professional portal authenticates the user (login + MFA) and then
     * opens the observer app with token credentials:
     *   ?token={jwt}&refreshToken={rt}&clientId={parentUserId}
     *
     * The observer does not perform its own login - it receives a valid JWT
     * from the portal that already has the Professional policy claim.
     */
    async function initializeFromAuthCode(code: string, parentUserId: string) {
      try {
        const token = code
        refreshTokenValue.value = null
        sessionStatus.value = 'idle'
        clearObservedClientState()

        if (!isSelectedClientUserId(parentUserId)) {
          sessionStatus.value = 'error'
          return false
        }

        // DEV bypass: if token starts with 'dev-' or 'poc-', use mock session
        if (
          import.meta.env.DEV &&
          (token.startsWith('dev-') || token.startsWith('poc-'))
        ) {
          professionalToken.value = `poc-token-${Date.now()}`
          professionalUserId.value = 'prof-user-123'
          professionalAccountId.value = '1001'
          professionalName.value = 'Jane Attorney'
          professionalEmail.value = 'jane@lawfirm.com'
          professionalRole.value = 'Attorney'
          observedParentUserId.value = parentUserId
          observedParentName.value = 'John Parent'
          sessionStatus.value = 'active'
          tokenExpiresAt.value = Date.now() + 30 * 60 * 1000
          return true
        }

        // Real flow: decode the JWT to extract professional identity
        const payload = decodeJwtPayload(token)
        if (!payload) {
          console.error('[SessionStore] JWT decode failed - payload is null')
          sessionStatus.value = 'error'
          return false
        }

        const expiry = getTokenExpiry(token)

        if (expiry <= Date.now()) {
          sessionStatus.value = 'expired'
          return false
        }

        professionalToken.value = token
        professionalAccountId.value = getClaimString(
          payload,
          'professional_account_id',
          'sub'
        )
        professionalUserId.value = getClaimString(payload, 'user_id', 'sub')
        professionalEmail.value = getClaimString(payload, 'email')
        professionalName.value = getClaimString(
          payload,
          'name',
          'professional_name'
        )
        professionalRole.value = getClaimString(
          payload,
          'role',
          'professional_role'
        )
        observedParentUserId.value = parentUserId
        sessionStatus.value = 'active'
        tokenExpiresAt.value = expiry

        return true
      } catch (err) {
        console.error('[SessionStore] initializeFromAuthCode EXCEPTION:', err)
        sessionStatus.value = 'error'
        return false
      }
    }

    /**
     * Store the refresh token separately (passed from portal handoff).
     */
    function setRefreshToken(token: string) {
      refreshTokenValue.value = token
    }

    /**
     * Refresh the access token using IdentityServer refresh_token grant.
     * POST /connect/token with grant_type=refresh_token
     */
    async function refreshToken(): Promise<boolean> {
      if (!refreshTokenValue.value) {
        sessionStatus.value = 'expired'
        return false
      }

      if (refreshTokenRequest) {
        return refreshTokenRequest
      }

      refreshTokenRequest = (async () => {
        try {
          const body = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: PROFESSIONAL_PORTAL_CLIENT_ID,
            refresh_token: refreshTokenValue.value as string
          })

          const { data } = await axios.post('/connect/token', body.toString(), {
            baseURL: IDENTITY_BASE_URL,
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          })

          const newAccessToken = data.access_token as string
          const newRefreshToken = data.refresh_token as string | undefined

          professionalToken.value = newAccessToken
          tokenExpiresAt.value = getTokenExpiry(newAccessToken)

          if (newRefreshToken) {
            refreshTokenValue.value = newRefreshToken
          }

          return true
        } catch {
          sessionStatus.value = 'expired'
          refreshTokenValue.value = null
          return false
        } finally {
          refreshTokenRequest = null
        }
      })()

      return refreshTokenRequest
    }

    function endSession() {
      sessionStatus.value = 'ended'
      clearSession()
    }

    /**
     * Fetch the observed client's info projection from the professional
     * read-only ClientInfo endpoint (single call: names, email, coparent,
     * timezone, date format). Populates the session display fields and
     * caches the full payload in clientInfo for the observer CommonStore.
     * Concurrent callers share one in-flight request.
     */
    async function fetchClientInfo(): Promise<void> {
      const targetClientId = observedParentUserId.value
      if (!targetClientId) return
      // Only reuse an in-flight request for the same client; a handoff to a
      // different client must fetch fresh data
      if (clientInfoRequest && clientInfoRequestFor === targetClientId) {
        return clientInfoRequest
      }

      clientInfoRequestFor = targetClientId
      clientInfoRequest = (async () => {
        try {
          const { observerApiService } = await loadObserverApiService()
          const info = await observerApiService.getClientInfo(targetClientId)

          // Discard if the session moved to a different client while pending
          if (observedParentUserId.value !== targetClientId) return

          if (!info) {
            console.warn('[SessionStore] GetClientInfo returned no payload')
            return
          }

          clientInfo.value = info

          if (info.fullName) {
            observedParentName.value = info.fullName
          }
          if (info.email) {
            observedParentEmail.value = info.email
          }
          if (info.coparentFullName) {
            observedCoparentName.value = info.coparentFullName
          } else {
            console.warn(
              '[SessionStore] GetClientInfo has no coparentFullName (client may be unmatched)'
            )
          }
        } catch (error) {
          // Non-critical - fall back to URL params or existing values
          console.warn(
            '[SessionStore] fetchClientInfo failed, using handoff params',
            error
          )
        } finally {
          // Only clear if a newer request has not replaced this one
          if (clientInfoRequestFor === targetClientId) {
            clientInfoRequest = null
            clientInfoRequestFor = null
          }
        }
      })()

      return clientInfoRequest
    }

    function clearSession() {
      professionalToken.value = null
      refreshTokenValue.value = null
      professionalUserId.value = null
      professionalAccountId.value = null
      professionalName.value = ''
      professionalEmail.value = ''
      professionalRole.value = ''
      clearObservedClientState()
      tokenExpiresAt.value = 0
    }

    return {
      // State
      professionalToken,
      refreshTokenValue,
      professionalUserId,
      professionalAccountId,
      professionalName,
      professionalEmail,
      professionalRole,
      observedParentUserId,
      observedParentName,
      observedParentEmail,
      observedCoparentName,
      clientInfo,
      sessionStatus,
      tokenExpiresAt,
      // Computed
      isProfessionalSession,
      selectedClientUserId,
      // Actions
      initializeFromAuthCode,
      setRefreshToken,
      refreshToken,
      fetchClientInfo,
      clearObservedClientState,
      endSession,
      clearSession
    }
  },
  {
    persist: {
      key: 'professional-session',
      storage: sessionStorage,
      pick: [
        'professionalToken',
        'refreshTokenValue',
        'professionalUserId',
        'professionalAccountId',
        'professionalName',
        'professionalEmail',
        'professionalRole',
        'observedParentUserId',
        'observedParentName',
        'observedParentEmail',
        'observedCoparentName',
        'sessionStatus',
        'tokenExpiresAt'
      ]
    }
  }
)
